// The notch: the Home button (root, or the webtop this browser set as Home), the favorite webtops and the recent ones,
// kept per browser in localStorage (webtop.recent, webtop.favorites, webtop.home) and refreshed on every render. Where
// it sits, snapped to an edge of the screen or anywhere between, is per browser and per screen size (webtop.notch).
import { iconUrl, isLight, lineIcon, SCALE } from '../widgets/kit.js';
import { currentColor } from './background.js';
import { $root, band, byTitle, safeArea } from './geometry.js';
import { appletService, ASSISTANT, AUDIO, LAUNCHER, serviceNow, STORAGE, WEBTOPS, withService } from './applets.js';
import { showMenu } from './menu.js';
import { goTo } from './nav.js';
import { addTab, closePanel, openPanel, togglePanel } from './notch-panel.js';
import { relayout } from './render.js';
import { setSlug, storageOf, webtop, slug, SLUG_RE } from './state.js';
import { canScroll, holdPageFrom } from './stillness.js';
import { slugOfPath, webtopHref } from '/model.js';
import { firstGlyph } from './tiles.js';
import { toast } from './ui.js';

// The notch holds buttons for the last NOTCH_MAX recent webtops (in alphabetical
// order, see renderNotch) and a button (webtop's logo) that toggles the launcher: a full-screen overlay with every webtop this
// browser visited recently. The list lives in localStorage (per browser, not on the server), newest first,
// capped at RECENT_MAX; render() refreshes the current webtop's entry on every change so titles and colours
// stay current, and every write re-renders the notch.
const RECENT_KEY = 'webtop.recent';
const RECENT_MAX = 12;
const NOTCH_MAX = 2;
const HOME_RECENT_MAX = 9; // three rows of three on the launcher's Home; its Recent Webtops section lists them all
const $notchRecent = document.getElementById('notch-recent');
const $launcherBtn = document.getElementById('launcher-btn');
const $talkBtn = document.getElementById('talk-btn');
const $micBtn = document.getElementById('notch-mic');
const $sink = document.getElementById('notch-sink');

function readList(key) {
  try {
    const list = JSON.parse(localStorage.getItem(key));
    return Array.isArray(list) ? list.filter((r) => r && SLUG_RE.test(r.slug)) : [];
  } catch { return []; }
}
function writeList(key, list, max) {
  try { localStorage.setItem(key, JSON.stringify(list.slice(0, max))); } catch {}
  renderNotch();
}
export const readRecent = () => readList(RECENT_KEY);
export const writeRecent = (list) => writeList(RECENT_KEY, list, RECENT_MAX);

// Favorite webtops (webtop.favorites): entries like the recents', in the order the user put them (a new one goes last;
// the launcher's Favorites section drags them about). The first two have buttons in the notch, before the recents, and
// all come first in the launcher; they are kept apart from the recents so that one stays a favorite however long ago
// it was visited (and survives Clear Recent).
const FAVORITES_KEY = 'webtop.favorites';
export const readFavorites = () => readList(FAVORITES_KEY);
const writeFavorites = (list) => writeList(FAVORITES_KEY, list);
export const isFavorite = (s) => readFavorites().some((r) => r.slug === s);
export function favoriteWebtop(r, on) {
  const rest = readFavorites().filter((f) => f.slug !== r.slug);
  const { at, ...entry } = r; // a recents entry's visit time means nothing here
  writeFavorites(on ? [...rest, entry] : rest);
}
// The favorites in the order of these slugs, as dragged in the launcher (one not named, if any, stays behind them).
function reorderFavorites(slugs) {
  const list = readFavorites();
  const place = (r) => { const at = slugs.indexOf(r.slug); return at < 0 ? slugs.length : at; };
  writeFavorites(list.sort((a, b) => place(a) - place(b)));
}
// The star in the webtop menu and a recent webtop's menu (`r`, this webtop by default), a quick action: hollow to
// add it, with a solid star inside (drawn pressed) to take it out.
export function favoriteMenuItem(r = currentEntry()) {
  const done = (on) => {
    favoriteWebtop(r, on);
    serviceNow(LAUNCHER)?.refresh();
    toast(on ? 'Added to Favorites' : 'Removed from Favorites');
  };
  return isFavorite(r.slug)
    ? { label: 'Unfavorite', quick: 'star-active', checked: true, action: () => done(false) }
    : { label: 'Favorite', quick: 'star', checked: false, action: () => done(true) };
}

// What the recents and favorites keep of the open webtop: its title and colour for the button, and how it is
// protected, for the launcher's marks (a password: `locked`; an owner: `owned`, `mine` when it is the signed-in
// user; whether this browser holds its keys: `unlocked`; where it is stored: `storage`, model.js), as of the last visit.
const currentEntry = () => ({
  slug, title: webtop.title, color: currentColor(),
  locked: !!webtop.locked, owned: !!webtop.owned, mine: !!webtop.mine, unlocked: !!webtop.unlocked, storage: storageOf(),
});

export function rememberWebtop() {
  const entry = currentEntry();
  const list = readRecent().filter((r) => r.slug !== slug);
  list.unshift({ ...entry, at: Date.now() });
  writeRecent(list);
  // A favorite keeps its place but follows renames and colour changes, as the recents do.
  const favorites = readFavorites();
  const at = favorites.findIndex((r) => r.slug === slug);
  if (at >= 0) { favorites[at] = entry; writeFavorites(favorites); }
  if (isHomePinned()) writeHome({ slug, title: webtop.title }); // keep the Home tooltip's title current
  else if (isHome()) renderHome(); // root as the default Home: its title comes from the recents list
}
// Out of the recents and the favorites too: Remove from Recents on a favorite, or a deleted webtop.
export function forgetWebtop(s) {
  writeRecent(readRecent().filter((r) => r.slug !== s));
  if (isFavorite(s)) favoriteWebtop({ slug: s }, false);
}
// A webtop that changed address (moved to another storage, applets/storage/src/moves.js): its recents and favorites entries and the
// Home pin follow it (the rest of an entry is refreshed on the next visit, which comes right after).
export function moveWebtopEntries(from, to) {
  const move = (r) => (r.slug === from ? { ...r, slug: to } : r);
  writeRecent(readRecent().map(move));
  writeFavorites(readFavorites().map(move));
  const h = readHomePin();
  if (h?.slug === from) writeHome({ ...h, slug: to });
}

// The coloured square with the first letter (or emoji) of the title, shared by launcher tiles and notch buttons.
export function glyphNode(r) {
  const glyph = document.createElement('span');
  glyph.className = 'glyph';
  glyph.classList.toggle('light', isLight(r.color));
  glyph.classList.toggle('plain', !r.color); // no colour of its own: a launcher card draws just the letter
  glyph.style.setProperty('--glyph-color', r.color || 'var(--bg)'); // the stylesheet decides how to paint it
  glyph.textContent = (firstGlyph(r.title) || '?').toUpperCase();
  return glyph;
}
export const recentLabel = (r) => `${r.locked ? '🔒 ' : ''}${r.title}`;

// A button for one webtop (a launcher or Webtops section tile, a notch button): click goes there, right-click offers
// Favorite / Unfavorite, Set as Home and, for one this browser remembers, removal (a webtop listed by the server
// may not be in the recents). The current webtop's button goes there too, which only closes the open overlay (goTo).
export function recentButton(r, cls) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = cls;
  b.classList.toggle('current', r.slug === slug);
  b.addEventListener('click', () => goTo(webtopHref(r.slug)));
  b.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    showMenu(e.clientX, e.clientY, [
      { label: recentLabel(r), cls: 'head' },
      { label: 'Open', action: () => goTo(webtopHref(r.slug)) },
      favoriteMenuItem(r),
      ...homeMenuItem(r),
      ...(isFavorite(r.slug) || readRecent().some((x) => x.slug === r.slug) ? [{
        label: 'Remove from Recents',
        quick: 'eject',
        action: () => { forgetWebtop(r.slug); serviceNow(LAUNCHER)?.refresh(); serviceNow(WEBTOPS)?.refresh(); },
      }] : []),
    ]);
  });
  return b;
}

function notchButton(r) {
  const b = recentButton(r, 'recent');
  const notes = `${isFavorite(r.slug) ? ', a favorite' : ''}${r.slug === slug ? ' — this webtop' : ''}`;
  b.title = `${recentLabel(r)} (${webtopHref(r.slug)})${notes}`;
  b.setAttribute('aria-label', b.title);
  b.append(glyphNode(r));
  return b;
}

// Recent webtops are shown in alphabetical order (by title, then address) in the notch and the launcher, so they keep
// their places while you move between them instead of reshuffling on every visit; the stored list stays newest first.
export const alphabetical = (list) => list.sort((a, b) => byTitle(a, b) || a.slug.localeCompare(b.slug));

// The recent webtops for a list: the favorites first, then the others newest first, without `skip`.
export function webtopLists(skip) {
  const favorites = readFavorites().filter((r) => r.slug !== skip);
  const seen = new Set([skip, ...favorites.map((r) => r.slug)]);
  return { favorites, recent: readRecent().filter((r) => !seen.has(r.slug)) };
}

// The Home webtop has its own button, so it is left out of the row (the launcher still lists it). The row holds the
// first favorites, in their order, then as many of the most recent others as fill it up to NOTCH_MAX, alphabetically.
export function renderNotch() {
  const { favorites, recent } = webtopLists(readHome()?.slug);
  const first = favorites.slice(0, NOTCH_MAX);
  const rest = recent.slice(0, NOTCH_MAX - first.length);
  $notchRecent.replaceChildren(...[...first, ...alphabetical(rest)].map(notchButton));
  renderSwitch();
}

// The notch starts with a house button that goes to the Home webtop: the root webtop (/) unless this browser
// pinned one (localStorage, {slug, title}; root itself can be pinned too). "Set as Home" / "Unset as Home" live in the webtop menu and on
// the button's own menu.
const HOME_KEY = 'webtop.home';
const $homeBtn = document.getElementById('home-btn');
const $homeIcon = $homeBtn.querySelector('.line-icon');

// The webtop explicitly set as Home, or null.
function readHomePin() {
  try {
    const h = JSON.parse(localStorage.getItem(HOME_KEY));
    return h && SLUG_RE.test(h.slug) ? h : null;
  } catch { return null; }
}
// The webtop the house button goes to: the pin, or root (titled from the recents list when it is there).
function readHome() {
  return readHomePin() ?? { slug: 'root', title: readRecent().find((r) => r.slug === 'root')?.title || 'webtop' };
}
export function writeHome(h) {
  try { h ? localStorage.setItem(HOME_KEY, JSON.stringify(h)) : localStorage.removeItem(HOME_KEY); } catch {}
  renderHome();
  renderNotch();
  serviceNow(LAUNCHER)?.refresh(); // its Home mark
}
export const isHomeWebtop = (s) => readHome().slug === s;
export const goHome = () => goTo('/', { home: true });
const isHome = () => isHomeWebtop(slug);
export const isHomePinned = (s = slug) => readHomePin()?.slug === s;
// The webtop an address in this browser's history means: `/` is this browser's Home, so a page opened there asks the
// API for that and the address stays as it is. root itself then goes by `/@root` (goTo), which a reload keeps.
export const addressSlug = (path) => (path === '/' ? readHomePin()?.slug ?? 'root' : slugOfPath(path));
// Run by client.js before anything reads the slug.
export function openHome() {
  if (location.pathname === '/') setSlug(addressSlug('/'));
}
// The house in the webtop menu and a recent webtop's menu (`r`, this webtop by default), a quick action (solid and
// drawn pressed on the Home webtop): Unset there, Set anywhere else, root included (Home is root anyway, but setting
// it makes that explicit and fills the notch's house).
const unpinItem = () => ({
  label: 'Unset as Home', quick: 'home-active', checked: true, action: () => { writeHome(null); toast('Home unset'); },
});
export function homeMenuItem(r = { slug, title: webtop.title }) {
  if (isHomePinned(r.slug)) return [unpinItem()];
  return [{
    label: 'Set as Home', quick: 'home', checked: false,
    action: () => { writeHome({ slug: r.slug, title: r.title }); toast('Set as Home'); },
  }];
}
export function renderHome() {
  const h = readHome();
  const here = h.slug === slug;
  $homeBtn.classList.toggle('active', here);
  // Painted in the Home webtop's colour, as the recents are: from its recents entry, or none (the stylesheet's
  // default) until this browser has visited it; on Home itself, the page's once the webtop has loaded.
  const color = here && webtop ? currentColor() : [...readRecent(), ...readFavorites()].find((r) => r.slug === h.slug)?.color;
  if (color) $homeBtn.style.setProperty('--glyph-color', color);
  else $homeBtn.style.removeProperty('--glyph-color');
  $homeBtn.classList.toggle('light', !!color && isLight(color));
  // The menu's house: solid while a webtop is pinned, an outline otherwise.
  $homeIcon.style.setProperty('--icon', iconUrl(readHomePin() ? 'home-active' : 'home'));
  $homeBtn.title = `Home: ${h.title} (${webtopHref(h.slug)})${here ? ' — this webtop' : ''}`;
  $homeBtn.setAttribute('aria-label', $homeBtn.title);
  renderSwitch();
}

// The pill is a list of webtops. Collapsed it shows only the one that is open, as a button that opens a menu to switch: Home,
// the favorites and the recents. It collapses when the user says so (`collapsed` in webtop.notch, per browser) and, for as long
// as it lasts and with nothing saved, when the notch's buttons do not fit (fitPill).
const $pill = $homeBtn.closest('.notch-pill');
const $switch = document.getElementById('notch-switch');
function readCollapsed() {
  try { return JSON.parse(localStorage.getItem(PLACE_KEY))?.collapsed === true; } catch { return false; }
}
// Measured expanded and put right before anything is painted, so whether it fits never depends on how it is drawn now. An
// open panel changes nothing here: the notch has the same largest size with it as without.
function fitPill() {
  if (readCollapsed()) return $pill.classList.add('collapsed');
  $pill.classList.remove('collapsed');
  $pill.classList.toggle('collapsed', $items.scrollWidth > $items.clientWidth || $items.scrollHeight > $items.clientHeight);
}
function setCollapsed(on) {
  try { localStorage.setItem(PLACE_KEY, JSON.stringify({ layouts: readLayouts(), ...(on ? { collapsed: true } : {}) })); } catch {}
  $pill.classList.remove('collapsed');
  fitPill();
}
// The open webtop as this browser knows it; on a locked one, or none, what the recents kept, or just its address.
const openEntry = () => (webtop && webtop.slug === slug ? currentEntry() : readRecent().find((r) => r.slug === slug) ?? { slug, title: slug });
// Home keeps its house (solid when pinned), anything else its first letter, each in its webtop's colour.
function renderSwitch() {
  const r = openEntry();
  if (isHome()) $switch.innerHTML = lineIcon(readHomePin() ? 'home-active' : 'home');
  else $switch.textContent = (firstGlyph(r.title) || '?').toUpperCase();
  if (r.color) $switch.style.setProperty('--glyph-color', r.color);
  else $switch.style.removeProperty('--glyph-color');
  $switch.classList.toggle('light', !!r.color && isLight(r.color));
  $switch.title = slug ? `${recentLabel(r)} (${webtopHref(slug)}): switch webtop` : 'Switch webtop';
  $switch.setAttribute('aria-label', $switch.title);
}
function switchItems() {
  const h = readHome();
  const { favorites, recent } = webtopLists(h.slug);
  const go = (r, icon) => ({ label: recentLabel(r), icon, radio: true, checked: r.slug === slug, action: () => goTo(webtopHref(r.slug)) });
  return [
    { label: 'Webtops', cls: 'head' },
    { ...go(h, 'home'), title: 'Home', action: goHome },
    ...favorites.map((r) => go(r, 'star-active')),
    ...alphabetical(recent).map((r) => go(r, 'clock')),
    'sep',
    // Collapsed only for want of room, there is nothing to expand: it does so by itself when there is room.
    ...(readCollapsed() ? [{ label: 'Expand', title: "Show the webtops' buttons again", action: () => setCollapsed(false) }] : []),
    ...(readHomePin() ? [unpinItem()] : []),
  ];
}
// At the pointer, or by the button when a key pressed it.
function openSwitch(e) {
  e.preventDefault();
  const r = $switch.getBoundingClientRect();
  showMenu(e.clientX || r.left, e.clientY || r.top, switchItems());
}

// Drag to delete: while tiles are dragged (client/lib/input.js) or a group member is (client/lib/groups.js), the notch shows only a
// trash can, and dropping there deletes what was dragged without asking. The whole notch counts as the trash, a
// bigger target than the icon, and the pointer is tested rather than the tile: the tile is kept clear of the notch.
const $notch = document.getElementById('notch');
const $items = document.getElementById('notch-items');
const $trash = document.getElementById('notch-trash');
export function showTrash(on) {
  if (on) closePanel();
  document.body.classList.toggle('trash-open', on);
  if (!on) $trash.classList.remove('over');
}
// Whether (x, y), screen px, is over the trash; marks it while it is.
export function overTrash(x, y) {
  if (!document.body.classList.contains('trash-open')) return false;
  const r = $notch.getBoundingClientRect();
  const over = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  $trash.classList.toggle('over', over);
  return over;
}

// Where the notch sits (webtop.notch): {layouts: {s, m, w, l}}, one place per screen size like a tile's layouts (band() in
// geometry.js; a size not placed yet has the default). A place is {dock}, snapped to the 'bottom' (the default), 'top',
// 'left' or 'right' of the screen and centred there, vertical on a side; or {x, y}, free and horizontal, its centre as
// fractions of the screen, so it keeps its place when the window changes size (drawn as dock 'free'). Its grip, the thin
// pill it is dragged by, is on the edge facing the screen when snapped, and on top when free.
const PLACE_KEY = 'webtop.notch';
const DOCKS = ['top', 'left', 'right'];
const FACING = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
const NOTCH_SNAP = 32; // unscaled px: how near the screen's edge the notch (at a side, the pointer) comes to snap there
const NOTCH_GAP = 10; // unscaled px between a snapped notch and the screen's edge (style.css)
const NOTCH_MARGIN = 4; // unscaled px kept between a free notch and the screen's edges
const GRIP_SLOP = 3; // px of movement before a press on the grip counts as a drag
const VIEWPORTS = { s: 'small', m: 'medium', w: 'wide', l: 'large' }; // the screen sizes (band, geometry.js), as the grip's tooltip names them
const $grip = document.getElementById('notch-grip');
const $bar = $grip.parentElement;
function readLayouts() {
  try { return JSON.parse(localStorage.getItem(PLACE_KEY))?.layouts || {}; } catch { return {}; }
}
function readPlace(of) {
  const p = readLayouts()[of];
  if (DOCKS.includes(p?.dock)) return { dock: p.dock };
  if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) return { dock: 'free', x: p.x, y: p.y };
  return { dock: 'bottom' };
}
function savePlace(p) {
  const layouts = { ...readLayouts(), [size]: p.dock === 'free' ? { x: p.x, y: p.y } : { dock: p.dock } };
  try { localStorage.setItem(PLACE_KEY, JSON.stringify({ layouts, ...(readCollapsed() ? { collapsed: true } : {}) })); } catch {}
}
let size = ''; // the screen size it is on, set by initGrip
let place = { dock: 'bottom' }; // what is saved for that size; during a drag the notch shows `shown`
let shown = place;
export const notchAtBottom = () => place.dock === 'bottom';

function drawPlace(p = shown) {
  shown = p;
  $notch.dataset.dock = p.dock;
  $notch.dataset.grip = FACING[p.dock] ?? 'top';
  $notch.style.removeProperty('bottom');
  $notch.style.removeProperty('max-height');
  $notch.classList.toggle('vertical', p.dock === 'left' || p.dock === 'right');
  // What keeps clear of the notch moves to the end it is at (style.css).
  for (const dock of DOCKS) document.body.classList.toggle(`notch-${dock}`, p.dock === dock);
  if (p.dock !== 'free') {
    $notch.style.removeProperty('left');
    $notch.style.removeProperty('top');
    return;
  }
  // Measured now it has its shape, so a wider notch stays whole.
  const r = $notch.getBoundingClientRect();
  const m = NOTCH_MARGIN * SCALE;
  if ($notch.classList.contains('open')) return drawOpenFree(p, r, m);
  const within = (v, half, size) => Math.max(half + m, Math.min(v * size, size - half - m));
  $notch.style.left = `${within(p.x, r.width / 2, $root.clientWidth)}px`;
  $notch.style.top = `${within(p.y, r.height / 2, $root.clientHeight)}px`;
}
// A free notch with its panel open: its bar stays where it was, and the panel takes the side with more room.
function drawOpenFree(p, r, m) {
  const W = $root.clientWidth, H = $root.clientHeight;
  const bar = $bar.offsetHeight;
  const middle = Math.max(bar / 2 + m, Math.min(p.y * H, H - bar / 2 - m));
  const up = middle > H / 2;
  $notch.dataset.grip = up ? 'top' : 'bottom';
  $notch.style.left = `${Math.max(r.width / 2 + m, Math.min(p.x * W, W - r.width / 2 - m))}px`;
  $notch.style.top = up ? 'auto' : `${middle - bar / 2}px`;
  $notch.style.bottom = up ? `${H - middle - bar / 2}px` : 'auto';
  $notch.style.maxHeight = `${(up ? middle + bar / 2 : H - middle + bar / 2) - m}px`;
}
// A finger's swipe on the notch: towards where the panel grows opens it, the other way, out, closes it. The panel grows from
// the grip's edge, so which way that is depends on where the notch sits: drawn when it is open, and for a free notch as
// drawOpenFree would draw it. The grip itself drags the notch, push to talk's button once it is held (`data-held`, the Assistant
// applet's) keeps the finger, and a slider is dragged; a panel that can still scroll that way scrolls first, and only a swipe
// from where it stops closes it.
const SWIPE = 24; // unscaled px along the way before a swipe counts
const SWIPE_NOT = '#notch-grip, #notch-trash, input[type=range]';
const WAYS = { top: [0, -1], bottom: [0, 1], left: [-1, 0], right: [1, 0] };
function panelWay() {
  if ($notch.classList.contains('open')) return WAYS[$notch.dataset.grip];
  if (shown.dock === 'free') return WAYS[shown.y > 0.5 ? 'top' : 'bottom'];
  return WAYS[FACING[shown.dock]];
}
function initSwipe() {
  let from = null; // where the finger started, or stopped being a scroll; null once there is no swipe to follow
  const at = (e) => ({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  $notch.addEventListener('touchstart', (e) => {
    from = e.touches.length === 1 && !e.target.closest?.(SWIPE_NOT) ? { ...at(e), last: at(e) } : null;
  }, { passive: true });
  $notch.addEventListener('touchmove', (e) => {
    if (!from) return;
    if (e.touches.length !== 1 || $talkBtn.dataset.held) { from = null; return; }
    const now = at(e);
    const [wx, wy] = panelWay();
    const open = $notch.classList.contains('open');
    if (open && canScroll(e.target, from.last.x - now.x, from.last.y - now.y)) from = { ...now, last: now };
    from.last = now;
    const dx = now.x - from.x, dy = now.y - from.y;
    const along = dx * wx + dy * wy; // towards where the panel grows
    const across = Math.abs(dx * wy) + Math.abs(dy * wx);
    if (Math.abs(along) < SWIPE * SCALE || Math.abs(along) < across * 2) return;
    if (along > 0 && !open) openPanel();
    else if (along < 0 && open) closePanel();
    else return;
    from = null; // one swipe, one change
  }, { passive: true });
}

// The Notch tab in its panel: what is about the notch itself. Position is where it sits on this screen size, as a drag there
// would put it; a notch dragged somewhere free also lists that, for as long as it is there.
const POSITIONS = [['top', 'Top'], ['bottom', 'Bottom'], ['left', 'Left'], ['right', 'Right']];
function notchSetting(title, note, control) {
  const row = Object.assign(document.createElement('div'), { className: 'notch-setting' });
  const text = Object.assign(document.createElement('div'), { className: 'notch-setting-text' });
  const noted = Object.assign(document.createElement('div'), { className: 'notch-note', textContent: note });
  text.append(Object.assign(document.createElement('span'), { textContent: title }), noted);
  row.append(text, control);
  return { row, noted };
}
function notchTab(el, { restyle, setDock }) {
  const select = Object.assign(document.createElement('select'), { className: 'notch-select' });
  select.setAttribute('aria-label', 'Position');
  select.addEventListener('change', () => setDock(select.value));
  const position = notchSetting('Position', '', select);
  syncPosition = () => {
    const options = [...POSITIONS, ...(place.dock === 'free' ? [['free', 'Free']] : [])];
    select.replaceChildren(...options.map(([value, textContent]) => Object.assign(document.createElement('option'), { value, textContent })));
    select.value = place.dock;
    position.noted.textContent = `On ${VIEWPORTS[size]} screens.`;
  };
  syncPosition();
  const button = Object.assign(document.createElement('button'), { type: 'button', className: 'notch-action', textContent: 'Restyle' });
  button.addEventListener('click', restyle);
  el.replaceChildren(position.row, notchSetting('Restyle', 'Put the notch back at the bottom on every screen size, with its webtops expanded.', button).row);
  return () => { syncPosition = null; };
}
let syncPosition = null; // redraws the Notch tab's Position while it is shown

// The place for where the drag holds the grip. A side goes by the pointer, as a wide notch would reach it long before the
// pointer did; the top and bottom by the free notch's edge, which hangs below the grip. A snapped notch lets go a snap's
// distance past the grip it is held by, wherever a free notch would be: its grip, or its shape, changes as it comes away.
function placeAt(x, y) {
  const W = $root.clientWidth, H = $root.clientHeight, sa = safeArea();
  const r = $notch.getBoundingClientRect();
  const thick = Math.min(r.width, r.height); // across it, whichever way it lies now
  const zone = NOTCH_SNAP * SCALE;
  const pull = thick + (NOTCH_GAP + NOTCH_SNAP) * SCALE;
  const top = sa.top * SCALE, bottom = H - sa.bottom * SCALE;
  if (shown.dock === 'left' && x < pull) return { dock: 'left' };
  if (shown.dock === 'right' && x > W - pull) return { dock: 'right' };
  if (shown.dock === 'top' && y < top + pull) return { dock: 'top' };
  if (x < zone) return { dock: 'left' };
  if (x > W - zone) return { dock: 'right' };
  if (y < top + zone) return { dock: 'top' };
  if (y + thick > bottom - zone) return { dock: 'bottom' };
  return { dock: 'free', x: x / W, y: (y + thick / 2) / H };
}
function initGrip() {
  let grab = null; // the press: {id, px, py, dx, dy, moved}, dx and dy from the pointer to the grip's middle
  $grip.addEventListener('pointerdown', (e) => {
    if (e.button || grab) return;
    e.preventDefault(); // no text selection, no focus change
    const g = $grip.getBoundingClientRect();
    grab = {
      id: e.pointerId, px: e.clientX, py: e.clientY, moved: false,
      dx: g.left + g.width / 2 - e.clientX, dy: g.top + g.height / 2 - e.clientY,
    };
    $grip.setPointerCapture(e.pointerId);
  });
  $grip.addEventListener('pointermove', (e) => {
    if (e.pointerId !== grab?.id) return;
    if (!grab.moved && Math.hypot(e.clientX - grab.px, e.clientY - grab.py) < GRIP_SLOP) return;
    if (!grab.moved) { grab.moved = true; $grip.classList.add('dragging'); closePanel(); }
    drawPlace(placeAt(e.clientX + grab.dx, e.clientY + grab.dy));
  });
  const end = (e, keep) => {
    if (e.pointerId !== grab?.id) return;
    const { moved } = grab;
    grab = null;
    $grip.classList.remove('dragging');
    if (!moved) { if (keep) togglePanel(); return; } // a press that stayed put is a click
    if (!keep) return drawPlace(place);
    const wasBottom = notchAtBottom();
    place = shown;
    if (place.dock === 'free') { // where it was drawn: a drag past the screen's edge is kept on the screen
      const r = $notch.getBoundingClientRect();
      place = { ...place, x: (r.left + r.width / 2) / $root.clientWidth, y: (r.top + r.height / 2) / $root.clientHeight };
      shown = place;
    }
    savePlace(place);
    if (wasBottom !== notchAtBottom()) relayout(); // the page's room for scrolling tiles out from behind it (render.js)
  };
  $grip.addEventListener('pointerup', (e) => end(e, true));
  $grip.addEventListener('pointercancel', (e) => end(e, false));
  $grip.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault(); // Space would also be push to talk
    if (!e.repeat) togglePanel({ focus: true });
  });
  // A window resized across a screen size (band, geometry.js) takes the notch to that size's place and flickers the grip:
  // tiles keep their spots and sizes per screen size too, so whoever is arranging them sees they are now working on
  // another. render.js's own resize handler, a frame later, then draws the tiles' scroll room for the new place.
  const toSize = () => {
    size = band();
    place = readPlace(size);
    drawPlace(place);
    $grip.title = `Click for controls, drag to move, viewport ${VIEWPORTS[size]}`;
    syncPosition?.();
  };
  toSize();
  // Restyle: as for a tile (menus.js), it loses its place on every screen size and goes back to the default.
  const restyle = () => {
    const wasBottom = notchAtBottom();
    try { localStorage.removeItem(PLACE_KEY); } catch {}
    toSize();
    fitPill();
    if (wasBottom !== notchAtBottom()) relayout();
    toast('Notch restyled');
  };
  const setDock = (dock) => {
    const wasBottom = notchAtBottom();
    place = { dock };
    drawPlace(place);
    savePlace(place);
    syncPosition?.();
    if (wasBottom !== notchAtBottom()) relayout();
  };
  addTab({ id: 'notch', label: 'Notch', icon: 'notch', order: 900, render: (el) => notchTab(el, { restyle, setDock }) });
  $grip.addEventListener('contextmenu', (e) => e.preventDefault()); // a finger held on it may yet drag it
  addEventListener('resize', fitPill); // a collapsed notch that fits does not resize as the room for it grows
  addEventListener('resize', () => {
    if (band() === size) return drawPlace();
    toSize();
    $grip.classList.remove('flicker');
    void $grip.offsetWidth; // a size crossed during the last flicker starts it over
    $grip.classList.add('flicker');
  });
  $grip.addEventListener('animationend', () => $grip.classList.remove('flicker'));
  // Its buttons come and go, and a free notch must stay whole. They may also come and go with the notch at its largest and
  // no change in its size, so those are watched as well.
  new ResizeObserver(() => { drawPlace(); fitPill(); }).observe($notch);
  new MutationObserver(fitPill).observe($items, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
  // A horizontal notch too wide for the screen takes a mouse's wheel, which only turns up and down, sideways.
  $items.addEventListener('wheel', (e) => {
    if (!e.deltaY || e.deltaX || $notch.classList.contains('vertical') || $items.scrollWidth <= $items.clientWidth) return;
    e.preventDefault();
    $items.scrollLeft += e.deltaY;
  }, { passive: false });
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
// The marks under a webtop's name, each an icon with its meaning as a tooltip: stored in this browser (the icon of the
// client storage that holds it, applets/storage/src/local.js), owned (by you or someone else), locked or unlocked here (only for a webtop with a password or an owner), a
// favorite, Home. The protection is as of this browser's last visit (the entry in webtop.recent), or as the server
// listed it (the Webtops section).
export function webtopMarks(r) {
  const marks = [];
  const kept = serviceNow(STORAGE)?.clientStorages().find((s) => s.value === r.storage); // the storage applet's
  if (kept) marks.push([kept.icon, `Stored in this browser: ${kept.label}`]);
  if (r.owned) marks.push(['owner', r.mine ? 'Yours' : 'Owned by someone else']);
  if (r.locked || r.owned) marks.push(r.unlocked ? ['unlock', 'Unlocked in this browser'] : ['lock', 'Locked']);
  if (isFavorite(r.slug)) marks.push(['star-active', 'Favorite']);
  if (isHomeWebtop(r.slug)) marks.push(['home-active', 'Home']);
  if (!marks.length) return [];
  const row = document.createElement('span');
  row.className = 'marks';
  row.append(...marks.map(([icon, title]) => {
    const mark = document.createElement('span');
    mark.className = 'mark';
    mark.title = title;
    mark.setAttribute('role', 'img');
    mark.setAttribute('aria-label', title);
    mark.innerHTML = lineIcon(icon);
    return mark;
  }));
  return [row];
}

// A webtop's tile in the launcher (the Recent Webtops column) and the Webtops section: an entry of the recents, or of
// the server's list.
export function launchTile(r) {
  const b = recentButton(r, 'launch');
  b.title = r.slug === slug ? 'This webtop (close the launcher)' : `Go to ${webtopHref(r.slug)}`;
  const name = document.createElement('span');
  name.className = 'name';
  name.textContent = r.title; // the path is only in the tooltip, the protection in the marks
  b.append(glyphNode(r), name, ...webtopMarks(r));
  return b;
}

// The launcher's Recent Webtops column (the Launcher applet draws it): the favorites in their order, then the other
// recent webtops in alphabetical order, matched by title and path.
function recentItems() {
  const { favorites, recent } = webtopLists();
  return [...favorites, ...alphabetical(recent)].map((r) => ({ text: `${r.title}\n${r.slug}`, tile: () => launchTile(r) }));
}

const favoriteItems = () => readFavorites().map((r) => ({ key: r.slug, text: `${r.title}\n${r.slug}`, tile: () => launchTile(r) }));

export function init() {
  // the notch's launcher button opens and closes the launcher (the Launcher applet's), which shows it pressed
  $launcherBtn.addEventListener('click', () => appletService(LAUNCHER).then((l) => l.toggle()).catch(toast));
  withService(LAUNCHER, (l) => {
    l.addColumn({ id: 'recent', title: 'Recent Webtops', order: 20, max: HOME_RECENT_MAX, across: 3, items: recentItems });
    l.addColumn({ id: 'favorites', title: 'Favorites', home: false, items: favoriteItems, reorder: reorderFavorites });
    l.addEntry({ id: 'recent', label: 'Recent Webtops', icon: 'clock', order: 40, column: 'recent', empty: 'No recent webtops yet.', text: 'Recent Webtops\nhistory' });
    l.addEntry({
      id: 'favorites', label: 'Favorites', icon: 'star', order: 50, column: 'favorites', text: 'Favorites',
      empty: 'No favorites yet. Star webtops from their menus.',
      note: 'Drag to put them in order: the first two get buttons in the notch.',
    });
  });
  // the voice button is push to talk's (the Assistant applet's), which shows it when the assistant can answer here
  withService(ASSISTANT, (a) => a.attach($talkBtn));
  // the sink and the live mark are the Audio applet's, which plays through the one and shows the other
  withService(AUDIO, (a) => a.attach({ sink: $sink, mark: $micBtn }));
  $homeBtn.addEventListener('click', goHome);
  $switch.addEventListener('click', openSwitch);
  $switch.addEventListener('contextmenu', openSwitch);
  $homeBtn.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const h = readHome();
    showMenu(e.clientX, e.clientY, [
      { label: `Home: ${h.title}`, cls: 'head' },
      { label: 'Open', action: goHome },
      ...(readHomePin() ? [unpinItem()] : []),
      'sep',
      { label: 'Collapse', title: 'Show only the open webtop here, and switch from its menu', action: () => setCollapsed(true) },
    ]);
  });
  initGrip();
  holdPageFrom($notch); // scrolling on it, or on anything in it, is never the page's
  initSwipe();
  fitPill();
  renderHome();
  renderNotch(); // a locked webtop never calls render(), but its notch should still show Home and the recents
}
